import React, { useContext, useEffect, useRef, useState } from "react";
import { NavigationType, useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { Utils } from "../Services/Utils";
import { SiteContext, UserContext } from "../Contexts";
import NoWebsite from "./ErrorPages/NoWebsite";
import PickWebsite from "./ErrorPages/PickWebsite";

type Phase = "checking" | "pickWebsite" | "noWebsite" | "ready";

type Props = { element: JSX.Element };

const AuthRoute: React.FC<Props> = ({ element }) => {
    const { currentUser }: any = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const [searchParams] = useSearchParams();
    const { currentSite, setCurrentSite }: any = useContext(SiteContext);

    const [phase, setPhase] = useState<Phase>("checking");
    // Once the active site has been resolved for this session, a POP navigation
    // (browser back/forward) must not silently switch it back to whatever
    // ?websiteId was current when that history entry was created.
    const hasResolvedSite = useRef(false);

    const websiteId = searchParams.get("websiteId") ? parseInt(searchParams.get("websiteId")!) : null;
    const returnUrl = location.pathname + location.search;

    useEffect(() => {
        if (!Utils.isUserLoggedIn(currentUser.id) && location.pathname !== "/login") {
            navigate("/401", { replace: true, state: { returnUrl } });
            return;
        }

        if (currentUser.isPasswordTemporary) {
            navigate("/SetPassword", { replace: true });
            setPhase("ready");
            return;
        }

        const sites = currentUser.websites ?? [];
        const isBackForwardReplay = navigationType === NavigationType.Pop && hasResolvedSite.current;
        hasResolvedSite.current = true;

        if (websiteId) {
            const match = sites.find((s: any) => s.id === websiteId);

            if (!match) {
                if (sites.length > 0) {
                    setPhase("pickWebsite");
                    return;
                }
                setPhase("ready");
                return;
            }

            if (currentSite.id !== match.id) {
                if (isBackForwardReplay) {
                    // The popped history entry carries a stale ?websiteId - keep the
                    // active site as-is and correct the URL instead of switching sites.
                    navigate(`${location.pathname}?websiteId=${currentSite.id}`, { replace: true });
                    setPhase("ready");
                    return;
                }
                setCurrentSite({
                    id: match.id,
                    domainName: match.domainName,
                    shortName: match.shortName,
                    settings: match.settings,
                });
            }
            setPhase("ready");
            return;
        }

        if (!Utils.hasUserPickedWebsite(currentSite?.id)) {
            if (sites.length === 0) {
                if (currentUser.isSuperAdmin) {
                    setPhase("ready");
                } else {
                    setPhase("noWebsite");
                }
                return;
            }

            if (sites.length === 1) {
                const s = sites[0];
                setCurrentSite({
                    id: s.id,
                    domainName: s.domainName,
                    shortName: s.shortName,
                    settings: s.settings
                });
                setPhase("ready");
                return;
            }

            setPhase("pickWebsite");
            return;
        }

        setPhase("ready");
    }, [currentUser, websiteId, currentSite?.id, navigate, location.pathname, returnUrl, setCurrentSite, navigationType]);

    if (phase === "ready") return element;
    if (phase === "noWebsite") return <NoWebsite isSuperAdmin={currentUser.isSuperAdmin} />;
    if (phase === "pickWebsite") return <PickWebsite />;

    return <></>;
};

export default AuthRoute;